import * as THREE from "three";
import type { GLTF } from "three/examples/jsm/loaders/GLTFLoader.js";
import type { AssetBundle, NatureKey } from "./assets";
import { toToon, bakeBaseAO } from "./toon";

// Inlined to avoid the world.ts ↔ world-grass.ts circular import.
const MAP_UNITS = 256;

// Scattered grass tufts. Quaternius `Grass_Common_Short` glTF placed via
// InstancedMesh on a jittered 1m grid, only where the terrain reads as flat
// grass biome. Geometry is split into 8x8 = 64 spatial chunks so frustum
// culling skips the ~75% of grass off-screen at typical MOBA zoom.
//
// Wind sway and tip color come from a tiny `onBeforeCompile` GLSL injection on
// the source Lambert material — no ShaderMaterial needed. Each tuft uses its
// world position as a phase seed so the field doesn't move in unison.

const CHUNK_COUNT = 8;
const CHUNK_SIZE = MAP_UNITS / CHUNK_COUNT;
const PLACEMENT_STEP = 1.0;          // 1m grid; one candidate per square meter
const SLOPE_GRAD_MAX = 0.4;          // skip steeper than ~22°; matches grass biome
const HEIGHT_LOW = 4.0;              // below = sand
const HEIGHT_HIGH = 19.0;            // above = snow
const PLACEMENT_PROB = 0.70;         // baseline probability per cell on grass
const TUFT_BASE_SCALE = 0.95;        // Quaternius tuft is ~1.3m tall; near-full-scale reads at MOBA zoom
const TUFT_SCALE_JITTER = 0.40;      // ±40%

// ─── Mixed-in foliage (ferns + mushrooms) ───────────────────────────────────
// Playtest ask: "replace some of the grass blades with more ferns and stuff."
// Ferns/mushrooms ride the SAME placement grid + hash stream as the grass tufts,
// so they read as part of the grass ground-cover layer (not the sparse
// scatter-detail pass). At each grass cell that PASSES the grass gate we roll
// the foliage hash: a hit converts that cell from a grass tuft into a fern (most
// common) or a mushroom (rarer) — keeping total instance counts flat-ish, the
// foliage replaces grass rather than piling on top of it.
//
// Eligible cells ≈ 65k × heightFade-weighted PLACEMENT_PROB ≈ ~30k grass cells.
// FERN_FRACTION of those become ferns; MUSH_FRACTION become mushrooms.
const FERN_FRACTION = 0.10;          // ~10% of grass cells → ferns (pervasive ground cover, low-thousands)
const MUSH_FRACTION = 0.012;         // ~1.2% of grass cells → mushrooms (light sprinkle)

// Ferns are baked at ~1.2m; keep them ground-cover sized so they don't tower
// over the ~1.3m grass. Mushrooms stay small.
const FERN_BASE_SCALE = 0.65;        // → ~0.6–1.0m fern fronds among the grass
const FERN_SCALE_JITTER = 0.30;      // ±30%
const MUSH_BASE_SCALE = 0.7;         // baked 0.4–0.9m → ~0.3–0.7m
const MUSH_SCALE_JITTER = 0.35;      // ±35%

const FERN_KEYS: NatureKey[] = ["exFern1A", "exFern2A"];
const MUSH_KEYS: NatureKey[] = ["exMush1A", "exMush2A", "exMush3A", "exMush4A", "exMush5A"];

// ─── Brightness lifts (playtest: "blades of grass and ferns seem too dark") ──
// The dusk/Primal directional sun + Lambert/toon shading crush ground foliage
// into near-black at dusk and night. These constants floor the brightness so
// grass and ferns stay readable/lush without blowing out to neon. Tunable.
const GRASS_COLOR = 0xc8ec7a;              // base albedo — vibrant warm lime (was 0xb5dd6c)
const GRASS_EMISSIVE = 0x16280c;           // dark-green floor so blades never sink to black at night
const GRASS_EMISSIVE_INTENSITY = 1.0;
const FOLIAGE_EMISSIVE = 0x16280c;         // matching green floor for ferns/mushrooms after toToon
const FOLIAGE_EMISSIVE_INTENSITY = 1.0;

export type GrassField = {
  group: THREE.Group;
  update: (dt: number) => void;
  count: number;
};

// Deterministic [0, 1) hash. Same seed → same value, no per-load shuffle.
function hash01(x: number, y: number, salt = 0): number {
  const v = Math.sin(x * 12.9898 + y * 78.233 + salt * 31.4159) * 43758.5453;
  return v - Math.floor(v);
}

// Cheap 4m-cell smoothed noise — same family as terrain fbm but kept local to
// avoid coupling files. Used as the density modulator: clumpier regions get
// more tufts than uniform ones.
function valueHash(ix: number, iz: number): number {
  const v = Math.sin(ix * 12.9898 + iz * 78.233) * 43758.5453;
  return v - Math.floor(v);
}
function densityNoise(x: number, z: number): number {
  const cell = 6;
  const px = x / cell;
  const pz = z / cell;
  const ix = Math.floor(px);
  const iz = Math.floor(pz);
  const fx = px - ix;
  const fz = pz - iz;
  const a = valueHash(ix, iz);
  const b = valueHash(ix + 1, iz);
  const c = valueHash(ix, iz + 1);
  const d = valueHash(ix + 1, iz + 1);
  const sx = fx * fx * (3 - 2 * fx);
  const sz = fz * fz * (3 - 2 * fz);
  return (a * (1 - sx) + b * sx) * (1 - sz) + (c * (1 - sx) + d * sx) * sz;
}

// Convert a Quaternius PBR material to flat-shaded Lambert with a wind+tip-color
// shader injection. Same Lambert→flat shading rule as world.ts/resources.ts
// applies (matches `14-rendering.md`); the additional wind GLSL fans out from
// each instance's world position so the field looks alive but not synchronized.
function makeGrassMaterial(src: THREE.Material, timeUniform: { value: number }): THREE.MeshLambertMaterial {
  const s = src as THREE.MeshStandardMaterial;
  // Brighten the base color: Quaternius grass texture × vertex colors × Lambert
  // shading × ACES tone-mapping compounds into near-silhouette dark. Lifting the
  // material color to a saturated warm-green pushes tufts toward League's vibrant
  // lime-emerald rather than the cooler olive that the source texture lands at.
  // Yellow-shifted to match the bumped BIOME_GRASS warmth in world.ts.
  // No flatShading: it derives face normals per-fragment via screen-space
  // derivatives, which silently overrides the up-facing-normal injection in
  // the vertex shader below. Smooth shading lets the injected normals reach
  // each fragment so the directional sun actually lights the blades.
  const out = new THREE.MeshLambertMaterial({
    map: s.map,
    color: new THREE.Color(GRASS_COLOR),
    // Flat green emissive floor: keeps blades readable/lush at dusk & night so
    // they never silhouette to black, mirroring the brighter floor characters use.
    emissive: new THREE.Color(GRASS_EMISSIVE),
    emissiveIntensity: GRASS_EMISSIVE_INTENSITY,
    vertexColors: s.vertexColors,
    transparent: s.transparent,
    opacity: s.opacity,
    alphaTest: Math.max(0.3, s.alphaTest),
    side: s.side,
  });
  out.alphaMap = s.alphaMap;
  out.onBeforeCompile = (shader) => {
    shader.uniforms.uTime = timeUniform;
    shader.vertexShader =
      "uniform float uTime;\n" +
      shader.vertexShader.replace(
        "#include <beginnormal_vertex>",
        `#include <beginnormal_vertex>
        // Fake up-facing normals: grass blades stand near-vertical so their face
        // normals point horizontally, which makes N·L ≈ 0 against the overhead
        // directional sun and the field reads as dim/silhouetted. Bias the normal
        // toward +Y so every fragment catches a high fraction of the sun's
        // intensity. 85% blend keeps a hint of the original face shading so
        // lighting still varies slightly between blade angles.
        objectNormal = normalize(mix(objectNormal, vec3(0.0, 1.0, 0.0), 0.85));`,
      ).replace(
        "#include <begin_vertex>",
        `#include <begin_vertex>
        // Wind sway: only the upper portion of the blade moves. Per-instance
        // phase seeded from instance world position so neighbours sway out of
        // sync. Two frequencies stacked for a less-mechanical feel.
        #ifdef USE_INSTANCING
          float phaseSeed = instanceMatrix[3].x * 0.21 + instanceMatrix[3].z * 0.17;
        #else
          float phaseSeed = position.x * 0.21 + position.z * 0.17;
        #endif
        float swayMask = clamp(transformed.y / 1.4, 0.0, 1.0);
        swayMask *= swayMask;  // ease — base stays anchored
        float sway1 = sin(phaseSeed + uTime * 1.6) * 0.12;
        float sway2 = sin(phaseSeed * 1.7 + uTime * 0.9) * 0.06;
        transformed.x += (sway1 + sway2) * swayMask;
        transformed.z += cos(phaseSeed * 0.85 + uTime * 1.2) * 0.08 * swayMask;`,
      );
  };
  return out;
}

function extractTuftRenderable(gltf: GLTF): { geom: THREE.BufferGeometry; mat: THREE.Material } {
  let found: { geom: THREE.BufferGeometry; mat: THREE.Material } | null = null;
  gltf.scene.traverse((node) => {
    const m = node as THREE.Mesh;
    if (!m.isMesh || found) return;
    const mat = Array.isArray(m.material) ? m.material[0]! : m.material;
    found = { geom: m.geometry, mat };
  });
  if (!found) throw new Error("Grass glTF has no Mesh node");
  return found;
}

// Ferns/mushrooms are GLTFs with their own (alphaMode=MASK) materials, unlike the
// grass-blade geometry. Run them through the SAME toToon({cutout:true})+bakeBaseAO
// path the static doodads use (doodads.ts extractRenderables) so the foliage matches
// the doodad look — flat toon bands + cutout alpha + baked contact AO. One GLTF can
// emit several primitives; render each as its own InstancedMesh sharing the matrices.
type FoliageRenderable = { geom: THREE.BufferGeometry; mat: THREE.Material };
function extractFoliageRenderables(gltf: GLTF): FoliageRenderable[] {
  const out: FoliageRenderable[] = [];
  gltf.scene.traverse((node) => {
    const m = node as THREE.Mesh;
    if (!m.isMesh) return;
    const mat = Array.isArray(m.material) ? m.material[0]! : m.material;
    bakeBaseAO(m.geometry);
    // No occlusion cutout: ferns/mushrooms are sub-hero-height ground cover and
    // shouldn't stipple away under the camera (playtest 2026-06-25). The wind
    // sway + rim suppression key off alphaTest, not the cutout opt, so they stay.
    const toon = toToon(mat, { noiseScale: 2.5 });
    // toToon's flat bands crush ferns/mushrooms to near-black at dusk; a faint
    // green emissive floors them above black so they read as lush ground cover.
    // Cutout alpha (alphaTest) set by toToon is untouched.
    const toonMesh = toon as THREE.MeshToonMaterial;
    if (toonMesh.isMeshToonMaterial) {
      toonMesh.emissive = new THREE.Color(FOLIAGE_EMISSIVE);
      toonMesh.emissiveIntensity = FOLIAGE_EMISSIVE_INTENSITY;
    }
    out.push({ geom: m.geometry, mat: toon });
  });
  if (out.length === 0) throw new Error("Foliage glTF has no Mesh node");
  return out;
}

export function buildGrass(
  sampleHeight: (x: number, z: number) => number,
  assets: AssetBundle,
): GrassField {
  const group = new THREE.Group();
  group.name = "grass";
  const timeUniform = { value: 0 };

  const renderable = extractTuftRenderable(assets.nature.grassCommonShort);
  const baseMat = makeGrassMaterial(renderable.mat, timeUniform);

  // Pass 1: enumerate placements, group by chunk.
  type Placement = { x: number; z: number; rot: number; scale: number };
  const chunkBins: Placement[][] = Array.from({ length: CHUNK_COUNT * CHUNK_COUNT }, () => []);
  // Foliage placements collected on the same grid; keyed by NatureKey so each
  // variant gets its own InstancedMesh set (matches the doodad grouping).
  const fernPlacements = new Map<NatureKey, THREE.Matrix4[]>();
  const mushPlacements = new Map<NatureKey, THREE.Matrix4[]>();
  const dummy = new THREE.Object3D();
  let total = 0;
  let fernTotal = 0;
  let mushTotal = 0;

  for (let gz = 0; gz < MAP_UNITS; gz += PLACEMENT_STEP) {
    for (let gx = 0; gx < MAP_UNITS; gx += PLACEMENT_STEP) {
      // Per-cell jitter inside the 1m square so the result doesn't look gridded.
      const jx = (hash01(gx, gz, 1) - 0.5) * 0.85;
      const jz = (hash01(gx, gz, 2) - 0.5) * 0.85;
      const x = gx + 0.5 + jx;
      const z = gz + 0.5 + jz;
      if (x < 0 || x >= MAP_UNITS - 1 || z < 0 || z >= MAP_UNITS - 1) continue;

      const h = sampleHeight(x, z);
      if (h < HEIGHT_LOW || h > HEIGHT_HIGH) continue;

      // Slope from finite differences — match the (1 - normal.y) feel without
      // recomputing normals. Cheap enough at 65k cells.
      const dx = sampleHeight(x + 0.5, z) - sampleHeight(x - 0.5, z);
      const dz = sampleHeight(x, z + 0.5) - sampleHeight(x, z - 0.5);
      const grad = Math.hypot(dx, dz);
      if (grad > SLOPE_GRAD_MAX) continue;

      // Density modulator: clumpier where noise is high. Roll fract-sin against
      // it; final probability also drops near biome boundaries (smooth fade).
      const density = densityNoise(x, z);
      const heightFade =
        Math.min(1, (h - HEIGHT_LOW) / 1.5) *
        Math.min(1, (HEIGHT_HIGH - h) / 1.5);
      const prob = PLACEMENT_PROB * (0.4 + density * 0.6) * heightFade;
      if (hash01(gx, gz, 3) > prob) continue;

      // Foliage roll: this grass cell may instead become a fern or mushroom, so
      // the foliage rides the exact placement the grass would have occupied (same
      // ground-cover layer). Independent hash salt so the choice is stable but
      // uncorrelated with the grass gate. Mushrooms win the narrow lower band,
      // ferns the next band, grass otherwise.
      const foliageRoll = hash01(gx, gz, 6);
      if (foliageRoll < MUSH_FRACTION) {
        const key = MUSH_KEYS[Math.floor(hash01(gx, gz, 7) * MUSH_KEYS.length)]!;
        const rot = hash01(gx, gz, 8) * Math.PI * 2;
        const scale = MUSH_BASE_SCALE * (1 - MUSH_SCALE_JITTER + hash01(gx, gz, 9) * MUSH_SCALE_JITTER * 2);
        const y = sampleHeight(x, z);
        dummy.position.set(x, y, z);
        dummy.rotation.set(0, rot, 0);
        dummy.scale.setScalar(scale);
        dummy.updateMatrix();
        let arr = mushPlacements.get(key);
        if (!arr) { arr = []; mushPlacements.set(key, arr); }
        arr.push(dummy.matrix.clone());
        mushTotal++;
        continue;
      }
      if (foliageRoll < MUSH_FRACTION + FERN_FRACTION) {
        const key = FERN_KEYS[Math.floor(hash01(gx, gz, 7) * FERN_KEYS.length)]!;
        const rot = hash01(gx, gz, 8) * Math.PI * 2;
        const scale = FERN_BASE_SCALE * (1 - FERN_SCALE_JITTER + hash01(gx, gz, 9) * FERN_SCALE_JITTER * 2);
        const y = sampleHeight(x, z);
        dummy.position.set(x, y, z);
        dummy.rotation.set(0, rot, 0);
        dummy.scale.setScalar(scale);
        dummy.updateMatrix();
        let arr = fernPlacements.get(key);
        if (!arr) { arr = []; fernPlacements.set(key, arr); }
        arr.push(dummy.matrix.clone());
        fernTotal++;
        continue;
      }

      const rot = hash01(gx, gz, 4) * Math.PI * 2;
      const scale = TUFT_BASE_SCALE * (1 - TUFT_SCALE_JITTER + hash01(gx, gz, 5) * TUFT_SCALE_JITTER * 2);
      const cx = Math.min(CHUNK_COUNT - 1, Math.floor(x / CHUNK_SIZE));
      const cz = Math.min(CHUNK_COUNT - 1, Math.floor(z / CHUNK_SIZE));
      chunkBins[cz * CHUNK_COUNT + cx]!.push({ x, z, rot, scale });
      total++;
    }
  }

  // Pass 2: per-chunk InstancedMesh. Setting boundingSphere per chunk lets
  // three.js's frustum culling skip off-screen chunks (the resources.ts
  // bug-on-instanced-mesh-bounding-sphere applies here too).
  for (let cz = 0; cz < CHUNK_COUNT; cz++) {
    for (let cx = 0; cx < CHUNK_COUNT; cx++) {
      const placements = chunkBins[cz * CHUNK_COUNT + cx]!;
      if (placements.length === 0) continue;

      const inst = new THREE.InstancedMesh(renderable.geom, baseMat, placements.length);
      inst.name = `grass:${cx},${cz}`;
      inst.castShadow = false;       // shimmers at this map size — spec line 31
      inst.receiveShadow = false;    // each tuft is too small to read shadow detail
      placements.forEach((p, i) => {
        const y = sampleHeight(p.x, p.z);
        dummy.position.set(p.x, y, p.z);
        dummy.rotation.set(0, p.rot, 0);
        dummy.scale.setScalar(p.scale);
        dummy.updateMatrix();
        inst.setMatrixAt(i, dummy.matrix);
      });
      inst.instanceMatrix.needsUpdate = true;

      // Chunk bounding sphere centred on chunk midpoint, radius covers chunk
      // diagonal + tallest tuft height. Without this the geometry's tiny
      // origin-centred sphere culls every chunk that doesn't include origin.
      const midX = (cx + 0.5) * CHUNK_SIZE;
      const midZ = (cz + 0.5) * CHUNK_SIZE;
      const midY = sampleHeight(midX, midZ);
      const sphereR = Math.hypot(CHUNK_SIZE, CHUNK_SIZE) * 0.5 + 2;
      inst.boundingSphere = new THREE.Sphere(new THREE.Vector3(midX, midY, midZ), sphereR);
      group.add(inst);
    }
  }

  // Pass 3: emit the mixed-in foliage as chunked InstancedMeshes (per-cell
  // frustum culling, same as the grass + doodads). Each variant's matrices are
  // split into CHUNK_SIZE grid cells so off-screen foliage is culled before
  // reaching a draw call.
  const emitFoliage = (
    placements: Map<NatureKey, THREE.Matrix4[]>,
    label: string,
  ): void => {
    for (const [key, matrices] of placements) {
      if (matrices.length === 0) continue;
      const renderables = extractFoliageRenderables(assets.nature[key]);
      // Bin matrices by chunk cell.
      const cells = new Map<number, THREE.Matrix4[]>();
      for (const m of matrices) {
        const cx = Math.min(CHUNK_COUNT - 1, Math.floor(m.elements[12]! / CHUNK_SIZE));
        const cz = Math.min(CHUNK_COUNT - 1, Math.floor(m.elements[14]! / CHUNK_SIZE));
        const ckey = cz * CHUNK_COUNT + cx;
        let arr = cells.get(ckey);
        if (!arr) { arr = []; cells.set(ckey, arr); }
        arr.push(m);
      }
      for (const [ckey, arr] of cells) {
        renderables.forEach((r, ri) => {
          const inst = new THREE.InstancedMesh(r.geom, r.mat, arr.length);
          inst.name = `${label}:${key}:${ri}#${ckey}`;
          inst.castShadow = false;     // small ground detail — same rule as doodadPlant / grass
          inst.receiveShadow = false;
          arr.forEach((m, i) => inst.setMatrixAt(i, m));
          inst.instanceMatrix.needsUpdate = true;
          inst.computeBoundingSphere(); // instance-spanning sphere → per-cell frustum cull
          group.add(inst);
        });
      }
    }
  };
  emitFoliage(fernPlacements, "grass-fern");
  emitFoliage(mushPlacements, "grass-mush");

  const update = (dt: number): void => {
    timeUniform.value += dt;
  };

  // Total reported as grass tufts; foliage counts logged for tuning.
  console.log(`[grass] mixed-in foliage: ${fernTotal} ferns, ${mushTotal} mushrooms`);
  return { group, update, count: total };
}
